import { isActivatableThreadEditor } from 'components/editors/thread/activatable';
import ThreadEditorVM from 'components/editors/thread/thread';
import { wccModules } from 'enums/wccModules';
import { RecordingHelpers } from 'helpers/recording';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { Topic } from 'models/topic';
import './postButton.html';
import './postButton.scss';

interface ActivityPostButtonVMConfig {
    taskId: SubscribableOrNullableValue<string>
    editor: SubscribableOrNullableValue<ThreadEditorVM<any>>
}

@injectable()
export default class ActivityPostButtonVM {
    private editor: Subscribable<ThreadEditorVM<any> | undefined>

    topic: Subscribable<Topic | undefined>

    constructor(
        @inject(wccModules.componentConfig) model: ActivityPostButtonVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
    ) {
        const taskId = ko.pureComputed(() => ko.unwrap(model.taskId));
        this.editor = ko.pureComputed(() => ko.unwrap(model.editor));

        const topicId = this.editor.pluck('topicId');
        const isEditorReady = this.editor.pluck(e => isActivatableThreadEditor(e), false);

        const topicManager = storage.get(TopicManager, { topicId });
        const isLoadingTopic = topicManager.pluck('loading', true);
        this.topic = topicManager.pluck('topic');

        const userManager = storage.get(CurrentUserManager, { discussionId: taskId });
        const isLoadingUser = userManager.pluck('loading', false);
        const user = userManager.pluck('person');
        const isRegularUser = user.pluck('isRegular', false);

        const title = ko.pureComputed(() => this.getTitle());

        const isNeeded = ko.pureComputed(() => this.checkIfButtonNeeded());        
        const isReady = ko.pureComputed(() => !isLoadingTopic() && !isLoadingUser() && isEditorReady());
        const isVisible = ko.pureComputed(() => isReady() && isRegularUser() && isNeeded() && title().length > 0);

        ko.applyBindingsToNode(info.element, {
            'visible': isVisible,
            'text': title,
            'click': () => this.activateEditor(),

            'css': {
                'task-activity__post-button': true
            }
        }, this);
    }

    private activateEditor() {
        this.editor.invokeNotNull(editor => {
            if (isActivatableThreadEditor(editor))
                editor.activate();
        });        
    }

    private getTitle() {
        const topic = this.topic();

        if (topic != undefined) {
            if (topic.isTextCommentsTopic()) {
                return labels.EnterAComment;
            } else if (topic.isSurvey()) {
                return labels.CompleteASurvey;
            } else if (topic.isMediaOnly() || topic.isDocumentsCollection()) {
                if (topic.parsedAllowedMediaTypes().includes(enums.ThreadMediaTypes.Images.value))
                    return labels.AddPhoto;
                else if (topic.parsedAllowedMediaTypes().includes(enums.ThreadMediaTypes.Video.value))
                    return labels.AddVideo;
                else if (topic.parsedAllowedMediaTypes().includes(enums.ThreadMediaTypes.Files.value))
                    return labels.AddFile;
            } else if (topic.isVoxPop()) {
                return labels.UploadAVideoBlog;
            } else if (topic.isHeatMap()) {
                return labels.PinAComment;
            } else if (topic.isPinBoard()) {
                return labels.CreatePinBoard;
            } else if (topic.isVideoPresentation()) {
                return labels.WatchTheVideo;
            } else if (topic.isCardSort() || topic.isCardScore()) {
                return labels.SortCards;
            } else if (topic.isMultipleComments()) {
                return labels.AnswerQuestions;
            } else if (topic.isScreenRecording()) {
                return RecordingHelpers.checkIfScreenRecordingSupported() ? labels.RecordScreen : labels.UploadARecording;
            } else if (topic.isAudioRecording()) {
                return labels.RecordAudio;
            }
        }

        return '';
    }

    private checkIfButtonNeeded() {
        const topic = this.topic();

        if (topic != undefined) {
            if (topic.isInformationOnlyTopic()) return false;

            if (!topic.shouldBeAnswered())
                return topic.isSurvey() && topic.surveyAllowsMultipleResponses() ||
                    (topic.isMediaOnly() || topic.isVoxPop()) || topic.commentsLimit() === 0; // 0 means unlimited comments

            return true;
        }

        return false;
    }
}